const LETTER_OR_NUMBER = /[\p{L}\p{N}]/u;
const MARK = /\p{M}/u;
const MATH_SYMBOL = /\p{Sm}/u;
const ARABIC = /\p{Script_Extensions=Arabic}/u;

function buildNormalizedText(value) {
  let text = '';
  const positions = [];
  let offset = 0;

  for (const originalCharacter of value) {
    const start = offset;
    const end = start + originalCharacter.length;
    offset = end;

    for (const character of originalCharacter.normalize('NFKD').toLowerCase()) {
      if (MARK.test(character)) continue;

      if (LETTER_OR_NUMBER.test(character)) {
        text += character;
        for (let unit = 0; unit < character.length; unit += 1) {
          positions.push({ start, end });
        }
      } else if (MATH_SYMBOL.test(character)) {
        // Keep source groups such as "Food + O2" distinct from "Food O2".
        if (text.length > 0 && text[text.length - 1] !== ' ') {
          text += ' ';
          positions.push({ start, end });
        }
        text += character;
        for (let unit = 0; unit < character.length; unit += 1) {
          positions.push({ start, end });
        }
        text += ' ';
        positions.push({ start, end });
      } else if (text.length > 0 && text[text.length - 1] !== ' ') {
        text += ' ';
        positions.push({ start, end });
      }
    }
  }

  return { text, positions };
}

function normalizeWord(value) {
  return buildNormalizedText(value).text.trim();
}

function findWholeMatch(text, value, startFrom) {
  let candidate = text.indexOf(value, startFrom);

  while (candidate !== -1) {
    const beforeIsBoundary = candidate === 0 || text[candidate - 1] === ' ';
    const after = candidate + value.length;
    const afterIsBoundary = after === text.length || text[after] === ' ';

    if (beforeIsBoundary && afterIsBoundary) return candidate;
    candidate = text.indexOf(value, candidate + 1);
  }

  return -1;
}

function extendTrailingMarks(text, end) {
  let cursor = end;

  while (cursor < text.length) {
    const codePoint = text.codePointAt(cursor);
    const character = String.fromCodePoint(codePoint);
    if (!MARK.test(character)) break;
    cursor += character.length;
  }

  return cursor;
}

function visualArabicText(value) {
  let text = '';
  const positions = [];
  let offset = 0;
  for (const line of value.match(/[^\r\n]+|[\r\n]+/gu) || []) {
    const units = [];
    for (const character of line) {
      const start = offset;
      offset += character.length;
      const previous = units.at(-1);
      const ltr = LETTER_OR_NUMBER.test(character) && !ARABIC.test(character);
      if (previous && (MARK.test(character) || (ltr && previous.ltr))) {
        previous.text += character;
        previous.end = offset;
      } else {
        units.push({ text: character, start, end: offset, ltr });
      }
    }
    if (ARABIC.test(line)) units.reverse();
    for (const unit of units) {
      text += unit.text;
      // Preserve UTF-16 offsets, including combined glyphs and LTR numbers.
      for (let index = 0; index < unit.text.length; index++) {
        positions.push({ start: unit.start + index, end: unit.start + index + 1 });
      }
    }
  }
  return { text, positions };
}

function normalizedVisualPage(pageText) {
  const visual = visualArabicText(pageText);
  const normalized = buildNormalizedText(visual.text);
  return {
    text: normalized.text,
    positions: normalized.positions.map(position => ({
      start: visual.positions[position.start].start,
      end: visual.positions[position.end - 1].end,
    })),
  };
}

function visualRange(pageText, normalizedPage, start, end) {
  const segments = [];
  for (let index = start; index < end; index++) {
    if (normalizedPage.text[index] === ' ') continue;
    const position = normalizedPage.positions[index];
    const next = { start: position.start, end: extendTrailingMarks(pageText, position.end) };
    const previous = segments.at(-1);
    if (previous) {
      const gapStart = Math.min(previous.end, next.end);
      const gapEnd = Math.max(previous.start, next.start);
      if (gapEnd <= gapStart || !normalizeWord(pageText.slice(gapStart, gapEnd))) {
        previous.start = Math.min(previous.start, next.start);
        previous.end = Math.max(previous.end, next.end);
        continue;
      }
    }
    segments.push(next);
  }
  if (!segments.length) return null;
  return segments.length === 1 ? segments[0] : { ...segments[0], segments };
}

function alignNormalizedPage(pageText, words, normalizedPage, strictVisual = false) {
  const ranges = [];
  let cursor = 0;
  let unresolvedSourceGap = false;

  for (const word of words) {
    if (unresolvedSourceGap || typeof word?.value !== 'string') {
      ranges.push(null);
      continue;
    }

    if (word.alignmentGapBefore === true) {
      // An empty span explicitly means only the recognizer inserted words.
      // Source omissions must be consumed before searching repeated PDF text.
      if (typeof word.sourceGapBefore !== 'string') {
        unresolvedSourceGap = true;
      } else if (word.sourceGapBefore.length > 0) {
        const gap = normalizeWord(word.sourceGapBefore);
        while (normalizedPage.text[cursor] === ' ') cursor += 1;
        if (!gap || findWholeMatch(normalizedPage.text, gap, cursor) !== cursor) {
          unresolvedSourceGap = true;
        } else {
          cursor += gap.length;
        }
      }
      if (unresolvedSourceGap) {
        ranges.push(null);
        continue;
      }
    }

    const normalizedWord = normalizeWord(word.value);
    if (!normalizedWord) {
      ranges.push(null);
      continue;
    }

    const matchStart = findWholeMatch(normalizedPage.text, normalizedWord, cursor);
    while (normalizedPage.text[cursor] === ' ') cursor += 1;
    if (matchStart === -1 || (strictVisual && matchStart !== cursor)) {
      ranges.push(null);
      continue;
    }

    const matchEnd = matchStart + normalizedWord.length;
    const firstPosition = normalizedPage.positions[matchStart];
    const lastPosition = normalizedPage.positions[matchEnd - 1];

    ranges.push(strictVisual ? visualRange(pageText, normalizedPage, matchStart, matchEnd) : {
      start: firstPosition.start,
      end: extendTrailingMarks(pageText, lastPosition.end)
    });
    cursor = matchEnd;
  }

  return ranges;
}

export function alignSpeechWordsToPageText(pageText, words) {
  if (!Array.isArray(words)) return [];
  if (typeof pageText !== 'string' || pageText.length === 0) return words.map(() => null);
  const direct = alignNormalizedPage(pageText, words, buildNormalizedText(pageText));
  if (direct.every(Boolean) || !words.some(word => ARABIC.test(word?.value || ''))) return direct;
  // Old Apryse PDFs may expose visual Arabic per line. Accept that orientation
  // only when every spoken group matches the exact source prefix in order.
  // Partial/repeated-word guesses must never create a highlight elsewhere.
  const visual = alignNormalizedPage(pageText, words, normalizedVisualPage(pageText), true);
  return visual.every(Boolean) ? visual : direct;
}

export function resolveArabicSpeechSelection(selectedText, words) {
  if (typeof selectedText !== 'string' || !ARABIC.test(selectedText)) return selectedText;
  const candidates = [...new Set([selectedText, visualArabicText(selectedText).text])];
  const values = words.map(word => normalizeWord(word?.value || '').replaceAll(' ', ''));
  const matches = new Map();
  for (const candidate of candidates) {
    const phrase = normalizeWord(candidate).replaceAll(' ', '');
    if (!phrase) continue;
    for (let start = 0; start < values.length; start++) {
      let combined = '';
      for (let end = start; end < values.length && combined.length < phrase.length; end++) {
        if (end > start && words[end]?.alignmentGapBefore) break;
        combined += values[end];
        if (combined === phrase) matches.set(`${start}:${end}`, candidate);
      }
    }
  }
  return matches.size === 1 ? matches.values().next().value : null;
}

export function findActiveWordIndex(words, timeMs) {
  if (!Array.isArray(words) || words.length === 0 || !Number.isFinite(timeMs)) {
    return -1;
  }

  let low = 0;
  let high = words.length - 1;
  let active = -1;

  while (low <= high) {
    const middle = (low + high) >> 1;
    const wordTime = words[middle]?.time;
    if (!Number.isFinite(wordTime)) return -1;

    if (wordTime <= timeMs) {
      active = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  if (active === -1) return -1;
  const word = words[active];
  if (word.endTime !== undefined && (
    !Number.isFinite(word.endTime)
    || word.endTime <= word.time
    || timeMs >= word.endTime
  )) return -1;

  return active;
}

export function isAutoplayBlockedError(error) {
  return Boolean(error && typeof error === 'object' && error.name === 'NotAllowedError');
}

export function shouldRegeneratePageAudio(error) {
  if (!error || typeof error !== 'object') return false;
  const status = Number(error.status);
  const code = error.data?.code;
  if (code === 'PAGE_AUDIO_STALE') return true;
  if (status !== 404) return false;
  return !code || code === 'AUDIO_MARKS_NOT_READY';
}

export async function tryPlayAudio(audio) {
  if (!audio || typeof audio.play !== 'function') {
    return {
      status: 'error',
      error: new TypeError('A playable audio element is required')
    };
  }

  try {
    await audio.play();
    return { status: 'playing', error: null };
  } catch (error) {
    return {
      status: isAutoplayBlockedError(error) ? 'blocked' : 'error',
      error
    };
  }
}
